/**
 * Insurance API - Insurance contracts, coverage, and exports
 * @module api/insurance
 */

import { apiRequest } from './core.js';

// =============================================================================
// INSURANCE CONTRACT API
// =============================================================================

/**
 * Insurance Contract API - Fleet insurance contracts
 */
export const insuranceAPI = {
  getAll: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return apiRequest(`/insurance_contract${query ? `?${query}` : ''}`);
  },
  getById: (id) => apiRequest(`/insurance_contract/${id}`),
  create: (contract) => apiRequest('/insurance_contract', {
    method: 'POST',
    body: JSON.stringify(contract),
  }),
  update: (id, contract) => apiRequest(`/insurance_contract/${id}`, {
    method: 'PUT',
    body: JSON.stringify(contract),
  }),
  delete: (id) => apiRequest(`/insurance_contract/${id}`, {
    method: 'DELETE',
  }),
  // Workflow actions
  workflow: {
    suspend: (id) => apiRequest(`/insurance_contract/${id}/suspend`, { method: 'POST' }),
    reactivate: (id) => apiRequest(`/insurance_contract/${id}/reactivate`, { method: 'POST' }),
    terminate: (id) => apiRequest(`/insurance_contract/${id}/terminate`, { method: 'POST' }),
  }
};

// =============================================================================
// INSURANCE COVERAGE BLOCK API
// =============================================================================

/**
 * Insurance Coverage Block API - Coverage details per contract
 */
export const insuranceCoverageBlockAPI = {
  getAll: () => apiRequest('/insurance_coverage_block?_limit=5000'),
  getById: (id) => apiRequest(`/insurance_coverage_block/${id}`),
  create: (block) => apiRequest('/insurance_coverage_block', {
    method: 'POST',
    body: JSON.stringify(block),
  }),
  update: (id, block) => apiRequest(`/insurance_coverage_block/${id}`, {
    method: 'PUT',
    body: JSON.stringify(block),
  }),
  delete: (id) => apiRequest(`/insurance_coverage_block/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// INSURANCE MODULE API (unified coverage management)
// =============================================================================

/**
 * Insurance Module API — Single source of truth for guarantees, coverage, premiums
 */
export const insuranceModuleAPI = {
  // Catalog
  getModels: () => apiRequest('/insurance/models'),
  getGuaranteeTemplates: () => apiRequest('/insurance/guarantee-templates'),

  // Contract coverage
  applyModel: (contractId, coverageModelId, overrides = {}) =>
    apiRequest(`/insurance/contract/${contractId}/apply-model`, {
      method: 'POST',
      body: JSON.stringify({ coverage_model_id: coverageModelId, overrides }),
    }),
  getContractCoverage: (contractId) =>
    apiRequest(`/insurance/contract/${contractId}/coverage`),
  updateCoverageBlock: (blockId, data) =>
    apiRequest(`/insurance/coverage-block/${blockId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  addCustomGuarantee: (contractId, data) =>
    apiRequest(`/insurance/contract/${contractId}/guarantee`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  removeGuarantee: (blockId) =>
    apiRequest(`/insurance/coverage-block/${blockId}`, { method: 'DELETE' }),

  // Vehicle assignment
  assignVehicle: (data) =>
    apiRequest('/insurance/vehicle/assign', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  removeVehicle: (viId, data = {}) =>
    apiRequest(`/insurance/vehicle/${viId}/remove`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
  getVehicleProfile: (vehicleId) =>
    apiRequest(`/insurance/vehicle/${vehicleId}/profile`),

  // Premiums
  getPremiumBreakdown: (contractId) =>
    apiRequest(`/insurance/contract/${contractId}/premiums`),
  setVehiclePremium: (viId, amount) =>
    apiRequest(`/insurance/vehicle-insurance/${viId}/premium`, {
      method: 'PUT',
      body: JSON.stringify({ amount }),
    }),
  setVehicleDeductible: (viId, amount) =>
    apiRequest(`/insurance/vehicle-insurance/${viId}/deductible`, {
      method: 'PUT',
      body: JSON.stringify({ amount }),
    }),
  autoAllocatePremiums: (contractId) =>
    apiRequest(`/insurance/contract/${contractId}/auto-allocate`, { method: 'POST' }),

  // Verification
  getVerificationStatus: (vehicleId) =>
    apiRequest(`/insurance/vehicle/${vehicleId}/verification`),
  getExpectedGuarantees: (viId) =>
    apiRequest(`/insurance/vehicle-insurance/${viId}/verification/expected`),
  declareGuarantees: (viId, guarantees) =>
    apiRequest(`/insurance/vehicle-insurance/${viId}/verification/declare`, {
      method: 'POST',
      body: JSON.stringify({ guarantees }),
    }),
  computeVerification: (viId) =>
    apiRequest(`/insurance/vehicle-insurance/${viId}/verification/compute`, { method: 'POST' }),
  saveVerification: (viId, data) =>
    apiRequest(`/insurance/vehicle-insurance/${viId}/verification/save`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getVerificationSummary: () =>
    apiRequest('/insurance/verification/summary'),
  getUnverifiedVehicles: () =>
    apiRequest('/insurance/verification/unverified'),
  getAllVerificationStatuses: () =>
    apiRequest('/insurance/verification/all-statuses'),
};

// =============================================================================
// FLEET INSURANCE EXPORT API (Arcadys format)
// =============================================================================

/**
 * Fleet Insurance Export API - Export for insurance renewals
 */
export const fleetInsuranceExportAPI = {
  // Preview export data (JSON)
  preview: (year, odometerReferenceDate = null) => {
    let url = `/exports/fleet-insurance/${year}/preview`;
    if (odometerReferenceDate) {
      url += `?odometer_reference_date=${odometerReferenceDate}`;
    }
    return apiRequest(url);
  },
  // Download Excel file
  download: async (year, odometerReferenceDate = null) => {
    let url = `/api/v1/exports/fleet-insurance/${year}`;
    if (odometerReferenceDate) {
      url += `?odometer_reference_date=${odometerReferenceDate}`;
    }
    const { getAuthHeaders } = await import('./core.js');
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error('Export failed');
    }
    const blob = await response.blob();
    // Trigger download
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `FLOTTE_VEHICULE_${year}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
    return { ok: true };
  },
  // Download PDF file
  downloadPDF: async (year, odometerReferenceDate = null) => {
    let url = `/api/v1/exports/fleet-insurance/${year}/pdf`;
    if (odometerReferenceDate) {
      url += `?odometer_reference_date=${odometerReferenceDate}`;
    }
    const { getAuthHeaders } = await import('./core.js');
    const response = await fetch(url, { headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error('Export PDF failed');
    }
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = `FLOTTE_ASSURANCE_${year}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(downloadUrl);
    return { ok: true };
  }
};
